import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { ApiResources } from "@/api/resources";
import Checkbox from "@/components/CheckBox/Checkbox";
import InputField from "@/components/InputField/InputField";
import PaperBox from "@/components/PaperBox/PaperBox";
import QueryDropdown from "@/components/QueryDropdown/QueryDropdown";
import {
  VDO_INTEGRATION_FIELD_NAMES,
  VdoErrors,
  VdoObjectState
} from "@/Pages/Integrations/types";
import { ID_TYPE, inputFieldMarginBottom } from "@/shared";
import Grid from "@mui/material/Grid";

import useVdoIntegrationsInputs from "./hooks/useVdoIntegrationsInputs";

interface VdoIntegrationContentProps {
  isLoading: boolean;
  vdoObject: VdoObjectState;
  errors: VdoErrors;
  setErrors: Dispatch<SetStateAction<VdoErrors>>;
  setVdoObject: Dispatch<SetStateAction<VdoObjectState>>;
  optionalApiKey?: boolean;
  hasInteracted?: boolean;
  setHasInteracted?: Dispatch<SetStateAction<boolean>>;
}

const VdoIntegrationContent = ({
  isLoading,
  errors,
  vdoObject,
  setErrors,
  setVdoObject,
  optionalApiKey,
  hasInteracted,
  setHasInteracted
}: VdoIntegrationContentProps) => {
  const { t } = useTranslation();

  const { handleChange: validateOnChange, handleBlur } =
    useVdoIntegrationsInputs(errors, setErrors);

  const handleChange = (field: VDO_INTEGRATION_FIELD_NAMES, value: any) => {
    setVdoObject(prev => {
      const getFieldValue = () => {
        if (field === VDO_INTEGRATION_FIELD_NAMES.Enabled) {
          return !prev.enabled;
        }

        if (field === VDO_INTEGRATION_FIELD_NAMES.IncludeSubCompanies) {
          return !prev.includeSubCompanies;
        }

        return value;
      };

      return {
        ...prev,
        [field]: getFieldValue()
      };
    });

    if (field !== VDO_INTEGRATION_FIELD_NAMES.Enabled) {
      validateOnChange(field, value);
    }

    if (setHasInteracted && !hasInteracted) {
      setHasInteracted(true);
    }
  };

  return (
    <Grid
      container
      size={12}
      alignItems="stretch"
      sx={{
        justifyContent: "flex-end"
      }}
      data-testid="vdo-integration-content-container-grid"
      spacing={3}
    >
      <Grid size={{ xs: 12, md: 6 }} sx={{ paddingTop: 0 }}>
        <PaperBox
          paperTitle={t("Dialog##vdo integration##provider settings title")}
          testId="vdo-integration-provider-settings-card"
          customStyling={{ padding: "24px 24px 8px 24px" }}
          paperDescription={t(
            "Dialog##vdo integration##provider settings description"
          )}
        >
          <InputField
            data-testid="vdo-integration-account-id-field"
            fullWidth
            required
            value={vdoObject[VDO_INTEGRATION_FIELD_NAMES.AccountId]}
            labelLeft={t("Dialog##account id")}
            placeholder={t("Dialog##enter here")}
            size="medium"
            onChange={e =>
              handleChange(
                VDO_INTEGRATION_FIELD_NAMES.AccountId,
                e.target.value
              )
            }
            onBlur={e =>
              handleBlur(VDO_INTEGRATION_FIELD_NAMES.AccountId, e.target.value)
            }
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />

          <InputField
            data-testid="vdo-integration-api-key-field"
            fullWidth
            required={!optionalApiKey}
            value={vdoObject[VDO_INTEGRATION_FIELD_NAMES.ApiKey]}
            labelLeft={t("Dialog##api key")}
            placeholder={t("Dialog##api key placeholder")}
            size="medium"
            onChange={e =>
              handleChange(VDO_INTEGRATION_FIELD_NAMES.ApiKey, e.target.value)
            }
            onBlur={e =>
              handleBlur(VDO_INTEGRATION_FIELD_NAMES.ApiKey, e.target.value)
            }
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
        </PaperBox>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }} sx={{ paddingTop: 0 }}>
        <PaperBox
          paperTitle={t("Settings##general settings")}
          testId="vdo-integration-general-settings-card"
          customStyling={{
            padding: "24px 24px 8px 24px"
          }}
        >
          <QueryDropdown
            testId="vdo-integration-company-dropdown"
            disableClearable
            resource={ApiResources.CompanyList}
            idType={ID_TYPE.Id}
            value={vdoObject[VDO_INTEGRATION_FIELD_NAMES.SelectedCompany]}
            readOnly={optionalApiKey}
            size="medium"
            fullWidth
            isRequired
            change={(_, val) =>
              handleChange(VDO_INTEGRATION_FIELD_NAMES.SelectedCompany, val)
            }
            labelLeft={t("Dialog##company")}
            textFieldParams={{ placeholder: t("Dialog##select") }}
            customStyle={inputFieldMarginBottom}
            isDisabled={isLoading}
          />

          <InputField
            data-testid="vdo-integration-name-field"
            size="medium"
            fullWidth
            required
            value={vdoObject[VDO_INTEGRATION_FIELD_NAMES.Name]}
            labelLeft={t("Dialog##integration name")}
            placeholder={t("Dialog##enter here")}
            onBlur={e =>
              handleBlur(VDO_INTEGRATION_FIELD_NAMES.Name, e.target.value)
            }
            onChange={e =>
              handleChange(VDO_INTEGRATION_FIELD_NAMES.Name, e.target.value)
            }
            errorText={errors[VDO_INTEGRATION_FIELD_NAMES.Name]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />

          <QueryDropdown
            testId="vdo-integrations-file-format-dropdown"
            useValueForEquality
            disableClearable
            fullWidth
            isRequired
            resource={ApiResources.UsersSettingsFileFormats}
            idType={ID_TYPE.Id}
            size="medium"
            isDisabled={isLoading}
            value={vdoObject[VDO_INTEGRATION_FIELD_NAMES.FileFormat]}
            change={(_, val) =>
              handleChange(VDO_INTEGRATION_FIELD_NAMES.FileFormat, val)
            }
            labelLeft={t("Dialog##file format")}
            customStyle={inputFieldMarginBottom}
            textFieldParams={{
              placeholder: t("Dialog##select file format example")
            }}
          />

          <Checkbox
            id="vdo-integration-include-sub-companies-checkbox"
            infoIconTooltip={t("Dialog##integrations##include files tooltip")}
            infoIconTooltipSize="medium"
            checked={vdoObject.includeSubCompanies}
            label={t(
              "Dialog##integrations##include files from all sub companies"
            )}
            onChange={e =>
              handleChange(
                VDO_INTEGRATION_FIELD_NAMES.IncludeSubCompanies,
                e.target.value
              )
            }
            disabled={isLoading}
            customStyle={{
              ...inputFieldMarginBottom,
              "@media (min-width: 840px) and (max-width: 875px)": {
                marginRight: "28px"
              }
            }}
          />

          <Checkbox
            id="vdo-integration-disable-integration-checkbox"
            infoIconTooltip={t(
              "Dialog##integrations##deactivate integration##tooltip"
            )}
            infoIconTooltipSize="medium"
            checked={!vdoObject.enabled}
            label={t("Dialog##integrations##deactivate integration##title")}
            onChange={e =>
              handleChange(VDO_INTEGRATION_FIELD_NAMES.Enabled, e.target.value)
            }
            disabled={isLoading}
            customStyle={{
              ...inputFieldMarginBottom,

              "@media (min-width: 840px) and (max-width: 875px)": {
                marginRight: "28px"
              }
            }}
          />
        </PaperBox>
      </Grid>
    </Grid>
  );
};

export default VdoIntegrationContent;
